import { useNavigate } from "react-router-dom";
import { Pencil, Trash2, User,ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import React from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

export default function ChatSidebar({
  chats,
  onNewChat,
  onSelectChat,
  onDeleteChat,
  onEditTitle,
  activeChatIndex,
  editingIndex,
  setEditingIndex,
}) {
  const user = useSelector((state) => state.profile.user);
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <aside className="w-64 bg-gray-800 text-white h-screen p-4 flex flex-col border-r border-gray-700">
      {/* Header */}
<div className="flex justify-between items-center mb-4">
        {/* Back Button and History Text Wrapper */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/dashboard/student")}
            className="text-gray-400 hover:text-white transition-colors p-1 rounded hover:bg-gray-700"
            title="Back to Dashboard"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h2 className="text-lg font-semibold">{t("chatSidebar.history")}</h2>
        </div>
        
        <button
          onClick={onNewChat}
          className="text-sm bg-[#2aabdc] hover:bg-[#0c1c5c] text-white px-3 py-1 rounded-md"
        >
          + {t("chatSidebar.new")}
        </button>
      </div>

      {/* Chat List */}
      <div className="flex-1 overflow-y-auto space-y-2">
        {chats.length === 0 && (
          <p className="text-gray-400 text-sm">{t("chatSidebar.noChats")}</p>
        )}
        {chats.map((chat, idx) => (
          <div
            key={idx}
            className={`flex items-center justify-between px-3 py-2 rounded-md text-sm ${
              idx === activeChatIndex
                ? "bg-[#b0fffb] text-gray-700 font-semibold"
                : "bg-gray-700 hover:bg-gray-600 text-white"
            }`}
          >
            {editingIndex === idx ? (
              <input
                autoFocus
                defaultValue={chat.title}
                onBlur={(e) =>
                  onEditTitle(chat.conversationId, e.target.value, idx)
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.target.blur();
                }}
                className="w-full bg-transparent border-b border-gray-300 focus:outline-none"
              />
            ) : (
              <button
                onClick={() => onSelectChat(idx)}
                className="text-left truncate w-full"
              >
                {chat.title || `${t("chatSidebar.chat")} ${idx + 1}`}
              </button>
            )}

            <div className="flex items-center ml-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingIndex(idx);
                }}
                title={t("chatSidebar.editTitle")}
                className="text-blue-400 hover:text-blue-600 mr-1"
              >
                <Pencil className="w-4 h-4" />
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteChat(chat.conversationId, idx);
                }}
                title={t("chatSidebar.deleteChat")}
                className="text-red-400 hover:text-red-600"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Talk to AI Tutor */}
      {user?.role !== "teacher" && (
        <div className="mt-6 mb-6 flex flex-col items-center justify-center">
          <motion.button
            onClick={() => navigate("/dashboard/chatbot/voicebot")}
            whileHover={{ scale: 1.1 }}
            className="relative flex items-center justify-center p-4 rounded-full shadow-xl text-white overflow-hidden transition-all bg-gradient-to-r from-[#2aabdc] to-[#0c1c5c] focus:ring-blue-500"
          >
            <User className="w-8 h-8 z-10" />
            {[...Array(3)].map((_, i) => (
              <motion.span
                key={i}
                className="absolute rounded-full border-2 border-white/40"
                style={{ width: 40 + i * 15, height: 40 + i * 15 }}
                animate={{ scale: [1, 1.4, 1], opacity: [0.6, 0.2, 0] }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  delay: i * 0.4,
                }}
              />
            ))}
          </motion.button>

          <span className="mt-2 text-sm font-semibold text-[#2aabdc] drop-shadow-[0_0_6px_#2aabdc]">
            {t("chatSidebar.talkToAi")}
          </span>
        </div>
      )}
    </aside>
  );
}
